import './dashboardGaragePage.scss';

import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import HeroGray from '../../assets/hero-gray.svg';
import logo from '../../assets/logo-default.svg';
import { NavigationPane } from '../../components/shared_new/NavigationPane/NavigationPane';
import { resetStore } from '../../store/_core/resetStore';
import useAnalyticStore from '../../store/analyticStore/useAnalyticStore';
import useApplyStore from '../../store/applyStore/useApplyStore';
import { getVehicleApplicationDescription } from '../../store/dashboardStore/functions/getVehicleApplicationDescription';
import { getVehicleDefaultImage } from '../../store/dashboardStore/functions/getVehicleDefaultImage';
import { getVehicleDescription } from '../../store/dashboardStore/functions/getVehicleDescription';
import { isVehicleApplicationRefandiType } from '../../store/dashboardStore/functions/isVehicleApplicationRefandiType';
import useDashboardStore from '../../store/dashboardStore/useDashboardStore';
import { FeatureFlag } from '../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../store/featureStore/hooks/useFeatureFlag';
import useNavigationPaneStore from '../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';
import ApplicationCard from './ApplicationCard';
import RefandiCard from './RefandiCard';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const { sendNewDashboardApplicationAnalytics } = useAnalyticStore();
  const {
    computed: { userFirstName },
  } = useUserStore();

  const {
    vehicles,
    fetchVehicles,
    fetchVehiclesFailedMessage,
    isFetchingVehicles,
  } = useDashboardStore();

  const isRefandiFeaturesEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_REFANDI_HIDDEN_FEATURES,
  );

  const newApplicationClicked = (): void => {
    resetStore(useApplyStore);

    sendNewDashboardApplicationAnalytics();

    navigate('/apply');
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  return (
    <div className="dashboard-garage-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.dashboardGarage.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.dashboardGarage.header.drawerButton}
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <main data-testid={locator.pages.dashboardGarage.main.root}>
            <NavigationPane />

            <div className="col">
              <div className="content head">
                <span>
                  <h1 data-testid={locator.pages.dashboardGarage.main.h1}>
                    {t('DashboardGaragePage.subtitle')}
                  </h1>
                  <h2 data-testid={locator.pages.dashboardGarage.main.h2}>
                    {userFirstName
                      ? t('DashboardGaragePage.title.withName', {
                          firstName: userFirstName,
                        })
                      : t('DashboardGaragePage.title.default')}
                  </h2>
                </span>
                <span>
                  {isRefandiFeaturesEnabled && (
                    <LoadingButton
                      color="primary"
                      data-testid={
                        locator.pages.dashboardGarage.main.newApplicationButton
                      }
                      endIcon={<></>}
                      loading={false}
                      loadingPosition="end"
                      type="button"
                      variant="contained"
                      fullWidth
                      onClick={newApplicationClicked}
                    >
                      {t('DashboardGaragePage.newApplication')}
                    </LoadingButton>
                  )}
                </span>
              </div>

              {!isFetchingVehicles && !isEmpty(fetchVehiclesFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.dashboardGarage.alert.error}
                    icon={false}
                    severity="error"
                  >
                    {fetchVehiclesFailedMessage}
                  </Alert>
                </div>
              )}

              {isFetchingVehicles && !fetchVehiclesFailedMessage && (
                <div className="content empty skeleton-loading" />
              )}

              {!isFetchingVehicles && isEmpty(vehicles) && (
                <ul className="content empty">
                  <li>
                    <img alt={''} src={HeroGray} />
                    <h3 data-testid={locator.pages.dashboardGarage.main.h3}>
                      You don&#39;t have any applications...
                    </h3>
                  </li>
                </ul>
              )}

              {!isFetchingVehicles && !isEmpty(vehicles) && (
                <ul
                  className="content cards"
                  data-testid={locator.pages.dashboardGarage.main.cardList}
                >
                  {vehicles
                    // sort by isRefandiType to show RefandiCard first
                    .sort(
                      (a, b) =>
                        Number(isVehicleApplicationRefandiType(b)) -
                        Number(isVehicleApplicationRefandiType(a)),
                    )
                    .map((vehicle, index) => {
                      if (isVehicleApplicationRefandiType(vehicle)) {
                        return (
                          <RefandiCard
                            key={vehicle.id}
                            testId={locator.pages.dashboardGarage.main.getCardListItem(
                              index,
                            )}
                          />
                        );
                      }

                      if (isRefandiFeaturesEnabled) {
                        return (
                          <ApplicationCard
                            applicationDescription={getVehicleApplicationDescription(
                              vehicle,
                            )}
                            defaultImage={getVehicleDefaultImage(vehicle)}
                            description={getVehicleDescription(vehicle)}
                            id={vehicle.id}
                            key={vehicle.id}
                            testId={locator.pages.dashboardGarage.main.getCardListItem(
                              index,
                            )}
                          />
                        );
                      }

                      return null;
                    })}

                  {vehicles.length === 1 && <li className="filler" />}
                </ul>
              )}

              <footer data-testid={locator.pages.dashboardGarage.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </div>
          </main>
        </Box>

        {/* Mobile Drawer */}
        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'DashboardGaragePage';
